import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Key, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, X } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const UserManagement = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [resetModal, setResetModal] = useState({ show: false, user: null });
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.getUsers({
        page,
        page_size: pageSize,
        role: roleFilter || undefined,
        search: search || undefined
      });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleResetPassword = async () => {
    if (!resetModal.user) return;
    
    setResetting(true);
    try {
      const res = await api.adminResetPassword(resetModal.user.id);
      setResetResult({
        success: true,
        message: res.data.message,
        tempPassword: res.data.temp_password || null,
        emailSent: res.data.email_sent
      });
    } catch (error) {
      setResetResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to reset password'
      });
    } finally {
      setResetting(false);
    }
  };

  const closeModal = () => {
    setResetModal({ show: false, user: null });
    setResetResult(null);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'administrator': return 'bg-purple-100 text-purple-800';
      case 'frontline_worker': return 'bg-blue-100 text-blue-800';
      case 'caregiver': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'administrator': return t('auth.administrator');
      case 'frontline_worker': return t('auth.frontlineWorker');
      case 'caregiver': return t('auth.caregiver');
      default: return role;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      <div className="min-h-screen bg-[#FAFAF9]" data-testid="user-management-page">
        <Navbar user={user} onLogout={onLogout} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-[#0F4C81] font-semibold mb-4 hover:underline"
              data-testid="back-button"
            >
              <ChevronLeft size={20} />
              <span>{t('common.back')}</span>
            </button>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111827] mb-2 font-['Nunito']" data-testid="page-title">
              {t('admin.userManagement')}
            </h1>
            <p className="text-lg text-[#4B5563]">
              {t('admin.manageUsersDescription')}
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-6">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={t('admin.searchUsers')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                  data-testid="search-input"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                className="h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-[#0F4C81] focus:ring-4 focus:ring-blue-100 transition-all outline-none min-w-[180px]"
                data-testid="role-filter"
              >
                <option value="">{t('admin.allRoles')}</option>
                <option value="caregiver">{t('auth.caregiver')}</option>
                <option value="frontline_worker">{t('auth.frontlineWorker')}</option>
                <option value="administrator">{t('auth.administrator')}</option>
              </select>
              <button
                type="submit"
                className="h-12 px-6 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors"
                data-testid="search-button"
              >
                {t('common.search')}
              </button>
            </form>
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Users size={48} className="mb-4" />
                <p>{t('admin.noUsersFound')}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead className="bg-[#F3F4F6]">
                      <tr>
                        <th className="text-left px-6 py-4 font-semibold text-[#111827]">{t('auth.name')}</th>
                        <th className="text-left px-6 py-4 font-semibold text-[#111827]">{t('auth.email')}</th>
                        <th className="text-left px-6 py-4 font-semibold text-[#111827]">{t('auth.role')}</th>
                        <th className="text-left px-6 py-4 font-semibold text-[#111827]">{t('admin.createdAt')}</th>
                        <th className="text-center px-6 py-4 font-semibold text-[#111827]">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-t border-slate-100 hover:bg-[#FAFAF9]" data-testid={`user-row-${u.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-[#0F4C81] to-[#1e5a9a] rounded-full flex items-center justify-center">
                                <span className="text-white font-bold">{u.name.charAt(0).toUpperCase()}</span>
                              </div>
                              <span className="font-semibold text-[#111827]">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-[#4B5563]">{u.email}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getRoleBadgeColor(u.role)}`}>
                              {getRoleDisplay(u.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-[#4B5563] text-sm">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => setResetModal({ show: true, user: u })}
                              className="inline-flex items-center space-x-2 px-4 py-2 bg-[#F59E0B] text-black rounded-lg font-semibold hover:bg-[#D97706] transition-colors text-sm"
                              data-testid={`reset-password-btn-${u.id}`}
                              disabled={u.id === user.id}
                              title={u.id === user.id ? "Cannot reset your own password here" : "Reset password"}
                            >
                              <Key size={16} />
                              <span>{t('admin.resetPassword')}</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                  <p className="text-sm text-[#4B5563]">
                    {t('admin.showingUsers', { start: (page - 1) * pageSize + 1, end: Math.min(page * pageSize, total), total })}
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded-lg border-2 border-slate-200 hover:border-[#0F4C81] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="prev-page"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="px-4 py-2 font-semibold text-[#111827]">
                      {page} / {totalPages || 1}
                    </span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      className="p-2 rounded-lg border-2 border-slate-200 hover:border-[#0F4C81] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="next-page"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reset-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-[#111827] font-['Nunito']">
                {resetResult ? (resetResult.success ? t('common.success') : t('common.error')) : t('admin.confirmResetPassword')}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            {!resetResult ? (
              <>
                <div className="flex items-start space-x-3 p-4 bg-[#FEF3C7] rounded-xl mb-6">
                  <AlertTriangle className="text-[#F59E0B] flex-shrink-0" size={24} />
                  <div>
                    <p className="text-sm text-[#92400E]">
                      {t('admin.resetPasswordWarning')}
                    </p>
                    <p className="text-sm font-semibold text-[#92400E] mt-2">
                      {resetModal.user?.name} ({resetModal.user?.email})
                    </p>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={closeModal}
                    className="flex-1 px-4 py-3 bg-gray-200 text-[#111827] rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                    data-testid="cancel-reset"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleResetPassword}
                    disabled={resetting}
                    className="flex-1 px-4 py-3 bg-[#EF4444] text-white rounded-xl font-semibold hover:bg-[#DC2626] transition-colors disabled:opacity-50"
                    data-testid="confirm-reset"
                  >
                    {resetting ? t('common.loading') : t('admin.resetPassword')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={`flex items-start space-x-3 p-4 rounded-xl mb-6 ${resetResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                  {resetResult.success ? (
                    <CheckCircle className="text-green-600 flex-shrink-0" size={24} />
                  ) : (
                    <AlertTriangle className="text-red-600 flex-shrink-0" size={24} />
                  )}
                  <div>
                    <p className={`text-sm ${resetResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {resetResult.message}
                    </p>
                    {resetResult.tempPassword && (
                      <div className="mt-3 p-3 bg-white border-2 border-green-200 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">{t('admin.tempPassword')}:</p>
                        <p className="font-mono font-bold text-lg text-[#0F4C81] select-all">{resetResult.tempPassword}</p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="w-full px-4 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors"
                  data-testid="close-modal"
                >
                  {t('common.close')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Copyright Footer */}
      <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
        <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
      </footer>
    </>
  );
};

export default UserManagement;
